import Cookies from 'js-cookie';

import { getPostHogBootstrapCookieName, getPostHogPersistenceKey } from '@/common/constants';
import { readJsonFromLocalStorage, safeLocalStorage } from '@/lib/storage/safe-storage';

export interface BootstrapCookieValue {
  distinctID?: string;
  sessionID?: string;
}

function readJsonCookie<T>(name: string): T | null {
  const value = Cookies.get(name);
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function readBootstrapCookie(): BootstrapCookieValue | null {
  return readJsonCookie<BootstrapCookieValue>(getPostHogBootstrapCookieName());
}

const WRITE_PROBE_KEY = 'bazak-ls-probe';

/**
 * Whether posthog will use localStorage, decided the way posthog decides it — by writing. A store
 * that reads but cannot be written (private mode, quota) makes posthog fall back to its cookie and
 * ignore everything localStorage holds, so trusting a read there suppresses a bootstrap posthog
 * then needs, and it mints a fresh id on every load instead.
 */
function localStorageUsable(): boolean {
  safeLocalStorage.setItem(WRITE_PROBE_KEY, '1');
  const usable = safeLocalStorage.getItem(WRITE_PROBE_KEY) === '1';
  safeLocalStorage.removeItem(WRITE_PROBE_KEY);
  return usable;
}

/** Whether posthog-js carries an identity from an earlier page load. Its `localStorage+cookie`
 *  persistence writes `distinct_id` to both, localStorage winning; the cookie is `SameSite=Lax`, so
 *  it only answers on the first-party surface. Optional-chained throughout because a corrupt entry
 *  parses to any JSON value, `null` included — and this runs at module scope, where a throw would
 *  take posthog's init down with it. */
export function hasStoredPostHogIdentity(): boolean {
  const key = getPostHogPersistenceKey();
  if (!key) return false;
  type Persisted = { distinct_id?: string };
  const stored = localStorageUsable()
    ? readJsonFromLocalStorage<Persisted | null>({ key, fallback: null })
    : null;
  return Boolean(stored?.distinct_id || readJsonCookie<Persisted>(key)?.distinct_id);
}
